import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { Profile } from 'src/app/shared/models/profile';
import _ from 'lodash';
import { Role } from 'src/app/shared/models/user/user';
import { AuthService as Auth0 } from '@auth0/auth0-angular';
import { catchError, map } from 'rxjs/operators';
import { InjectorModule } from '../shared/modules/injector.module';

@Injectable()
export class AuthService {
  userProfile: Profile;
  personId: string;
  accessToken: string;
  expiresAt: string;
  idToken: string;

  auth0SDKIntialised: boolean = false;
  requestedScopes: string = 'openid profile email manage:teams offline_access';
  private _hasAuthenticated: BehaviorSubject<{
    authenticated: boolean;
    redirect_uri?: string;
  }> = new BehaviorSubject<{ authenticated: boolean; redirect_uri?: string }>({
    authenticated: false
  });
  public readonly hasAuthenticated: Observable<{
    authenticated: boolean;
    redirect_uri?: string;
  }> = this._hasAuthenticated.asObservable();

  private auth0: Auth0;

  constructor(
    private location: Location,
    private appConfig: AppConfigService,
    private http: HttpClient
  ) {}
  public get isAuthenticated$(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }
  public init() {
    this.auth0 = InjectorModule.injector.get(Auth0);

    this.auth0.idTokenClaims$.subscribe((idTokenPayload) => {
      this.setRole(idTokenPayload);
    });
    this.auth0.user$.subscribe((profile) => {
      if (profile) {
        this.userProfile = new Profile(profile);
      } else {
        this.userProfile = null;
      }
    });

    this.auth0SDKIntialised = true;

    this._hasAuthenticated.next({ authenticated: this.isAuthenticated });
  }
  public getToken(): string {
    return this.accessToken;
  }

  public forceLogin(setRedirectUri = true): void {
    if (setRedirectUri) {
      this.setRedirectUri();
    }
    this.removeSession();
    this.login(sessionStorage.getItem('loggedInConnection'));
  }

  public login(connection?: string): void {
    if (!connection) {
      if (!this.appConfig.tenant) return;
      connection = this.appConfig.tenant.defaultConnection;
    }
    sessionStorage.setItem('loggedInConnection', connection);
    this.auth0.loginWithRedirect({
      authorizationParams: {
        connection: connection
      }
    });
  }
  public setRedirectUri() {
    if (!sessionStorage.getItem('redirect_uri')) {
      sessionStorage.setItem('redirect_uri', this.location.path());
    }
  }
  public getNewToken(): Observable<string> {
    return this.auth0
      .getAccessTokenSilently({
        detailedResponse: true
      })
      .pipe(
        map((authResult) => {
          this.setSession(authResult);
          return authResult.access_token;
        }),
        catchError((err) => {
          return of(null);
        })
      );
  }
  public logout(): void {
    this.removeSession();
    sessionStorage.removeItem('redirect_uri');
    // Navigate to Auth0 to clear out any sso cookie data
    this.auth0.logout({ logoutParams: { returnTo: this.appConfig.logoutRedirectUri } }).subscribe();
  }

  public get isAuthenticated(): boolean {
    // Check whether the access token exists and has not expired
    return this.accessTokenExists && !this.accessTokenExpired;
  }

  public hasBeenAuthenticated(): Observable<{ authenticated: boolean; redirect_uri?: string }> {
    return this.hasAuthenticated;
  }

  public hasAnyRole(roleNames: Array<string>): boolean {
    const roles = JSON.parse(localStorage.getItem('roles'));

    if (roles) {
      let matched = _.filter(roleNames, (roleName) => roles.indexOf(roleName) != -1);
      return matched.length > 0;
    }

    return false;
  }

  public getProfile(): Observable<Profile> {
    if (this.userProfile) {
      return of(this.userProfile);
    }

    return this.auth0.user$.pipe(
      map((profile) => {
        return new Profile(profile);
      })
    );
  }

  public getPersonId(): Observable<string | null> {
    if (this.personId) {
      return of(this.personId);
    }

    const url = `${this.appConfig.baseApiUrl}v0.1/auth0/users/me`;
    return this.http.get<{ personId: string }>(url).pipe(
      map((res) => res.personId),
      tap((personId) => (this.personId = personId)),
      catchError(() => of(null))
    );
  }

  public getRole(): string {
    const roles = JSON.parse(localStorage.getItem('roles'));
    if (roles?.length) {
      return roles[0];
    }
    return null;
  }

  public isStaff(): boolean {
    return this.hasRole(Role.Staff);
  }

  public isOwner() {
    return this.hasRole(Role.Owner) && !this.isStaff();
  }

  public isAdmin(): boolean {
    return this.hasRole(Role.Admin) && !this.isOwner();
  }

  public isManager() {
    return this.hasRole(Role.Manager) && !this.isAdmin();
  }

  public isEditor() {
    return this.hasRole(Role.Editor) && !this.isManager();
  }

  public get staffLevelAccess(): boolean {
    return this.isStaff();
  }
  public get ownerLevelAccess(): boolean {
    return this.isStaff() || this.isOwner();
  }
  public get adminLevelAccess(): boolean {
    return this.isStaff() || this.isOwner() || this.isAdmin();
  }
  public get managerLevelAccess(): boolean {
    return this.isStaff() || this.isOwner() || this.isAdmin() || this.isManager();
  }
  public get editorLevelAccess(): boolean {
    return (
      this.isStaff() || this.isOwner() || this.isAdmin() || this.isManager() || this.isEditor()
    );
  }

  public get accessTokenExists(): boolean {
    // const accessToken = this.getToken();
    return this.accessToken != null || this.accessToken != undefined;
  }
  public get accessTokenExpired(): boolean {
    const expiresAt = JSON.parse(this.expiresAt);
    return new Date().getTime() >= expiresAt;
  }

  private hasRole(role: string): boolean {
    const roles = JSON.parse(localStorage.getItem('roles'));

    if (roles && roles.indexOf(role.toLowerCase()) > -1) {
      return true;
    }

    return false;
  }

  public setSession(authResult): void {
    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify(authResult.expires_in * 1000 + new Date().getTime());
    this.accessToken = authResult.access_token;
    this.idToken = authResult.id_token;
    this.expiresAt = expiresAt;
  }

  public setRole(idTokenPayload) {
    if (idTokenPayload) {
      const roles = idTokenPayload['https://lineupteams.com/roles'];
      localStorage.setItem('roles', JSON.stringify(roles || ['user']));
    }
  }

  private removeSession(): void {
    // Remove tokens and expiry time from localStorage
    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = null;
    this.personId = null;
    localStorage.removeItem('roles');
    this._hasAuthenticated.next({ authenticated: false });
  }
}
